// src/components/admin/AdminStats.tsx
// Seção de Estatísticas - IDENTIDADE GRAIA - ISOLADO

import React, { useState } from 'react';
import {
    BarChart3, Trash2, RefreshCw, AlertTriangle, BookOpen,
    Layers, ScrollText, HelpCircle, CreditCard, TrendingUp, Search,
    CheckCircle, AlertCircle, XCircle, ChevronLeft, ChevronRight
} from 'lucide-react';
import { SystemStats, UserData } from './types';
import './AdminStats.css';
import WorkHoursChart from './WorkHoursChart';
import UserEngagementModal from './UserEngagementModal';
import { useEngagementStats, useEngagementUsers, usePlatformContentStats } from '../../hooks/useAdminQueries';

interface AdminStatsProps {
    systemStats: SystemStats | null;
    allUsers: UserData[];
    onResetExcludedCards: () => void;
    onResetMyStats: () => void;
    onResetGlobalStats: () => void;
    onResetUserStats: (userId: string, username: string) => void;
}

type StatsTab = 'graia' | 'engagement';

const AdminStats: React.FC<AdminStatsProps> = ({
    systemStats,
    onResetExcludedCards,
    onResetMyStats,
    onResetGlobalStats,
    onResetUserStats
}) => {

    const [activeTab, setActiveTab] = useState<StatsTab>('graia');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

    const { data: platformData } = usePlatformContentStats(activeTab === 'graia' && !!systemStats);
    const { data: engagementStatsData, isLoading: loadingEngagement } = useEngagementStats(activeTab === 'engagement');
    const { data: engagementUsersData, isLoading: loadingEngagementUsers } = useEngagementUsers(
        activeTab === 'engagement',
        page,
        30
    );

    const platformStats = {
        totalDecks: platformData?.content?.totalDecks ?? 0,
        totalSubdecks: platformData?.content?.totalSubdecks ?? 0,
        totalApostilas: platformData?.content?.totalApostilas ?? 0,
        totalQuestions: platformData?.content?.totalQuestions ?? 0,
        totalCards: platformData?.content?.totalCards ?? 0,
    };

    const engagementStats = engagementStatsData ?? null;
    const engagementUsers = engagementUsersData?.users ?? [];
    const totalUsers = engagementUsersData?.total ?? 0;
    const totalPages = engagementUsersData?.totalPages ?? 1;
    const loadingAny = loadingEngagement || loadingEngagementUsers;

    const filteredUsers = engagementUsers.filter((user: any) =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handlePageChange = (newPage: number) => {
        if (newPage < 1 || newPage > totalPages) return;
        setPage(newPage);
        setSearchTerm('');
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active': return <CheckCircle size={16} className="adm-est-status-icon active" />;
            case 'risk': return <AlertCircle size={16} className="adm-est-status-icon risk" />;
            default: return <XCircle size={16} className="adm-est-status-icon inactive" />;
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'active': return 'Ativo';
            case 'risk': return 'Risco';
            default: return 'Inativo';
        }
    };

    const formatRelativeTime = (isoTimestamp: string | null): string => {
        if (!isoTimestamp) return 'Nunca';
        const diffMs = Date.now() - new Date(isoTimestamp).getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        if (diffHours < 1) return 'Há menos de 1h';
        if (diffHours < 24) return `Há ${diffHours}h`;
        if (diffDays === 1) return 'Ontem';
        if (diffDays < 30) return `Há ${diffDays} dias`;
        return new Date(isoTimestamp).toLocaleDateString('pt-BR');
    };

    return (
        <div className="adm-est-container adm-page">
            <div className="adm-page-header">
                <h2 className="adm-page-title">Estatísticas</h2>
                <p className="adm-page-subtitle">
                    Avalie números administrativos e de usuários
                </p>
            </div>

            <div className="adm-tabs">
                <button
                    className={`adm-tab ${activeTab === 'graia' ? 'active' : ''}`}
                    onClick={() => setActiveTab('graia')}
                >
                    <BarChart3 size={18} strokeWidth={1.5} />
                    Estatísticas GRAIA
                </button>
                <button
                    className={`adm-tab ${activeTab === 'engagement' ? 'active' : ''}`}
                    onClick={() => setActiveTab('engagement')}
                >
                    <TrendingUp size={18} strokeWidth={1.5} />
                    Engajamento Real
                </button>
            </div>

            {/* ── ABA GRAIA ── */}
            {activeTab === 'graia' && (
                <div className="adm-tab-content">
                    <div className="adm-est-reset-actions">
                        <button className="adm-est-btn-reset" onClick={onResetExcludedCards}>
                            <Trash2 size={16} />
                            Resetar Flashcards Excluídos
                        </button>
                        <button className="adm-est-btn-reset" onClick={onResetMyStats}>
                            <RefreshCw size={16} />
                            Resetar Minhas Estatísticas
                        </button>
                        <button className="adm-est-btn-reset danger" onClick={onResetGlobalStats}>
                            <AlertTriangle size={16} />
                            Resetar Estatísticas Globais
                        </button>
                    </div>

                    <div className="adm-est-platform-stats">
                        <h3>Estatísticas da Plataforma</h3>
                        <div className="adm-est-stats-grid-five">
                            <div className="adm-est-stat-card">
                                <div className="adm-est-stat-icon">
                                    <BookOpen size={32} strokeWidth={1.5} />
                                </div>
                                <div className="adm-est-stat-info">
                                    <div className="adm-est-stat-value">{platformStats.totalDecks}</div>
                                    <div className="adm-est-stat-label">Total de Decks</div>
                                </div>
                            </div>

                            <div className="adm-est-stat-card">
                                <div className="adm-est-stat-icon">
                                    <Layers size={32} strokeWidth={1.5} />
                                </div>
                                <div className="adm-est-stat-info">
                                    <div className="adm-est-stat-value">{platformStats.totalSubdecks}</div>
                                    <div className="adm-est-stat-label">Total de Subdecks</div>
                                </div>
                            </div>

                            <div className="adm-est-stat-card">
                                <div className="adm-est-stat-icon">
                                    <ScrollText size={32} strokeWidth={1.5} />
                                </div>
                                <div className="adm-est-stat-info">
                                    <div className="adm-est-stat-value">{platformStats.totalApostilas}</div>
                                    <div className="adm-est-stat-label">Total de Apostilas</div>
                                </div>
                            </div>

                            <div className="adm-est-stat-card">
                                <div className="adm-est-stat-icon">
                                    <HelpCircle size={32} strokeWidth={1.5} />
                                </div>
                                <div className="adm-est-stat-info">
                                    <div className="adm-est-stat-value">{platformStats.totalQuestions}</div>
                                    <div className="adm-est-stat-label">Total de Questões</div>
                                </div>
                            </div>

                            <div className="adm-est-stat-card">
                                <div className="adm-est-stat-icon">
                                    <CreditCard size={32} strokeWidth={1.5} />
                                </div>
                                <div className="adm-est-stat-info">
                                    <div className="adm-est-stat-value">{platformStats.totalCards}</div>
                                    <div className="adm-est-stat-label">Total de Flashcards</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <WorkHoursChart />
                </div>
            )}

            {/* ── ABA ENGAGEMENT ── */}
            {activeTab === 'engagement' && (
                <div className="adm-tab-content">
                    {loadingAny ? (
                        <div className="adm-est-loading-container">
                            <div className="adm-est-spinner"></div>
                            <p>Carregando dados de engajamento</p>
                        </div>
                    ) : (
                        <>
                            {engagementStats && (
                                <>
                                    <div className="adm-est-engagement-funnel">
                                        <div className="adm-est-funnel-item funnel-total">
                                            <div className="adm-est-funnel-content">
                                                <div className="adm-est-funnel-value">{engagementStats.totalUsers}</div>
                                                <div className="adm-est-funnel-label">Total de Usuários</div>
                                            </div>
                                        </div>
                                        <div className="adm-est-funnel-arrow">↓</div>
                                        <div className="adm-est-funnel-item">
                                            <div className="adm-est-funnel-content">
                                                <div className="adm-est-funnel-value">{engagementStats.activeThisMonth}</div>
                                                <div className="adm-est-funnel-label">Últimos 30 dias</div>
                                                <div className="adm-est-funnel-percent">
                                                    {((engagementStats.activeThisMonth / engagementStats.totalUsers) * 100).toFixed(0)}%
                                                </div>
                                            </div>
                                        </div>
                                        <div className="adm-est-funnel-arrow">↓</div>
                                        <div className="adm-est-funnel-item">
                                            <div className="adm-est-funnel-content">
                                                <div className="adm-est-funnel-value">{engagementStats.activeThisWeek}</div>
                                                <div className="adm-est-funnel-label">Últimos 7 dias</div>
                                                <div className="adm-est-funnel-percent">
                                                    {((engagementStats.activeThisWeek / engagementStats.totalUsers) * 100).toFixed(0)}%
                                                </div>
                                            </div>
                                        </div>
                                        <div className="adm-est-funnel-arrow">↓</div>
                                        <div className="adm-est-funnel-item">
                                            <div className="adm-est-funnel-content">
                                                <div className="adm-est-funnel-value">{engagementStats.activeToday}</div>
                                                <div className="adm-est-funnel-label">Hoje</div>
                                                <div className="adm-est-funnel-percent">
                                                    {((engagementStats.activeToday / engagementStats.totalUsers) * 100).toFixed(0)}%
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="adm-est-alert-cards">
                                        <div className="adm-est-alert-card warning">
                                            <div className="adm-est-alert-content">
                                                <div className="adm-est-alert-value">{engagementStats.inactive7Days}</div>
                                                <div className="adm-est-alert-label">7-14 dias sem estudar</div>
                                            </div>
                                        </div>
                                        <div className="adm-est-alert-card danger">
                                            <div className="adm-est-alert-content">
                                                <div className="adm-est-alert-value">{engagementStats.inactive14Days}</div>
                                                <div className="adm-est-alert-label">14-30 dias sem estudar</div>
                                            </div>
                                        </div>
                                        <div className="adm-est-alert-card critical">
                                            <div className="adm-est-alert-content">
                                                <div className="adm-est-alert-value">{engagementStats.inactive30Days}</div>
                                                <div className="adm-est-alert-label">&gt;30 dias inativos</div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="adm-est-users-engagement-section">
                                <div className="adm-est-engagement-header">
                                    <h3>
                                        Usuários por Engajamento
                                        {totalUsers > 0 && (
                                            <span className="adm-est-user-count">{totalUsers} usuários</span>
                                        )}
                                    </h3>
                                    <div className="adm-est-search-bar">
                                        <Search size={16} className="adm-est-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Filtrar nesta página"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {filteredUsers.length === 0 ? (
                                    <div className="adm-est-empty-state">
                                        <p>Nenhum usuário encontrado</p>
                                    </div>
                                ) : (
                                    <div className="adm-est-user-list">
                                        {filteredUsers.map((user: any) => (
                                            <button
                                                key={user._id}
                                                className="adm-est-user-card"
                                                onClick={() => setSelectedUserId(user._id)}
                                            >
                                                <div className="adm-est-user-card-avatar">
                                                    {user.username.charAt(0).toUpperCase()}
                                                </div>

                                                <div className="adm-est-user-card-info">
                                                    <span className="adm-est-user-card-name">{user.username}</span>
                                                    <span className="adm-est-user-card-last">
                                                        {formatRelativeTime(user.lastStudyEvent)}
                                                    </span>
                                                </div>

                                                <span className={`adm-est-status-badge ${user.status}`}>
                                                    {getStatusIcon(user.status)}
                                                    {getStatusLabel(user.status)}
                                                </span>

                                                <div className="adm-est-mini-heatmap">
                                                    {(user.activityLast7Days || Array(7).fill(0)).map((count: number, i: number) => {
                                                        const max = Math.max(...(user.activityLast7Days || [1]), 1);
                                                        const intensity = count > 0 ? Math.max(0.2, count / max) : 0;
                                                        return (
                                                            <div
                                                                key={i}
                                                                className="adm-est-heatmap-dot"
                                                                style={{
                                                                    opacity: count > 0 ? intensity : 0.08,
                                                                    backgroundColor: count > 0 ? '#003BD1' : undefined
                                                                }}
                                                                title={`${count} cards`}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {totalPages > 1 && !searchTerm && (
                                    <div className="adm-est-pagination">
                                        <button
                                            className="adm-est-page-btn"
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={page <= 1}
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <span className="adm-est-page-info">
                                            Página {page} de {totalPages}
                                        </span>
                                        <button
                                            className="adm-est-page-btn"
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={page >= totalPages}
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            {selectedUserId && (
                <UserEngagementModal
                    userId={selectedUserId}
                    onClose={() => setSelectedUserId(null)}
                    onResetStats={(uid, username) => {
                        onResetUserStats(uid, username);
                        setSelectedUserId(null);
                    }}
                />
            )}
        </div>
    );
};

export default AdminStats;
